import { useState, useCallback, useRef, useEffect } from 'react';
import { BlockStack, TextField, Button, InlineStack, Text, Banner, Card, Listbox, Popover } from '@shopify/polaris';
import { SHOPIFY_FIELD_OPTIONS } from '../constants/shopifyFieldOptions';

// Generate autocomplete suggestions from SHOPIFY_FIELD_OPTIONS
const generateAutocompleteSuggestions = () => {
  const suggestions = {
    'order.': [],
    'billing.': [],
    'shipping.': [],
    'customer.': [],
    'lineItem.': [],
    'product.': [],
    'variant.': [],
    'custom.': []
  };

  SHOPIFY_FIELD_OPTIONS.forEach(option => {
    if (option.disabled || !option.value) return;
    
    const value = option.value;
    
    // Check metafields first (they have longer prefixes)
    if (value.startsWith('metafield.order.custom.')) {
      // Extract custom metafield name (e.g., "metafield.order.custom.shipping_and_handling" -> "shipping_and_handling")
      const field = value.replace('metafield.order.custom.', '');
      if (!suggestions['custom.'].includes(field)) {
        suggestions['custom.'].push(field);
      }
    } else if (value.startsWith('metafield.product.custom.')) {
      // Add product custom metafields (same naming as Shopify)
      const field = value.replace('metafield.product.custom.', '');
      if (!suggestions['custom.'].includes(field)) {
        suggestions['custom.'].push(field);
      }
    } else if (value.startsWith('metafield.customer.custom.')) {
      // Add customer custom metafields
      const field = value.replace('metafield.customer.custom.', '');
      if (!suggestions['custom.'].includes(field)) {
        suggestions['custom.'].push(field);
      }
    } else if (value.startsWith('metafield.variant.custom.')) {
      // Add variant custom metafields
      const field = value.replace('metafield.variant.custom.', '');
      if (!suggestions['custom.'].includes(field)) {
        suggestions['custom.'].push(field);
      }
    } else if (value.startsWith('order.')) {
      // Extract field name from value (e.g., "order.orderNumber" -> "orderNumber")
      const field = value.replace('order.', '');
      if (!suggestions['order.'].includes(field)) {
        suggestions['order.'].push(field);
      }
    } else if (value.startsWith('billing.')) {
      const field = value.replace('billing.', '');
      if (!suggestions['billing.'].includes(field)) {
        suggestions['billing.'].push(field);
      }
    } else if (value.startsWith('shipping.')) {
      const field = value.replace('shipping.', '');
      if (!suggestions['shipping.'].includes(field)) {
        suggestions['shipping.'].push(field);
      }
    } else if (value.startsWith('customer.')) {
      const field = value.replace('customer.', '');
      if (!suggestions['customer.'].includes(field)) {
        suggestions['customer.'].push(field);
      }
    } else if (value.startsWith('lineItem.')) {
      const field = value.replace('lineItem.', '');
      if (!suggestions['lineItem.'].includes(field)) {
        suggestions['lineItem.'].push(field);
      }
    } else if (value.startsWith('product.')) {
      const field = value.replace('product.', '');
      if (!suggestions['product.'].includes(field)) {
        suggestions['product.'].push(field);
      }
    } else if (value.startsWith('variant.')) {
      const field = value.replace('variant.', '');
      if (!suggestions['variant.'].includes(field)) {
        suggestions['variant.'].push(field);
      }
    }
  });

  // Sort all suggestions alphabetically
  Object.keys(suggestions).forEach(key => {
    suggestions[key].sort();
  });

  console.log('Generated autocomplete suggestions:', suggestions);
  return suggestions;
};

const AUTOCOMPLETE_SUGGESTIONS = generateAutocompleteSuggestions();
console.log('AUTOCOMPLETE_SUGGESTIONS loaded:', AUTOCOMPLETE_SUGGESTIONS);

// JavaScript keywords and common patterns
const JS_KEYWORDS = [
  'let', 'const', 'var', 'if', 'else', 'return', 'function',
  'String', 'Number', 'Boolean', 'Array', 'Object',
  'toLowerCase', 'toUpperCase', 'trim', 'includes', 'replace',
  'startsWith', 'endsWith', 'split', 'join', 'substring'
];

export function CodeEditor({ value, onChange, onTest, testResults, testLoading }) {
  const [localValue, setLocalValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);

  useEffect(() => {
    console.log('State updated:', { showSuggestions, suggestionsCount: suggestions.length, suggestions });
  }, [showSuggestions, suggestions]);

  const handleChange = useCallback((newValue) => {
    setLocalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  }, [onChange]);

  const handleTest = useCallback(() => {
    if (onTest) {
      onTest(localValue);
    }
  }, [onTest, localValue]);

  // Get suggestions based on current cursor position
  const getSuggestions = useCallback((text, cursorPos) => {
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastWord = textBeforeCursor.split(/[\s\(\)\{\}\[\];,]/).pop() || '';
    
    console.log('getSuggestions called:', { lastWord, cursorPos });
    
    // Check for object property access (e.g., "order.", "custom.")
    for (const [prefix, fields] of Object.entries(AUTOCOMPLETE_SUGGESTIONS)) {
      if (lastWord.endsWith(prefix)) {
        const result = fields.map(field => prefix + field);
        console.log(`Matched prefix "${prefix}":`, result);
        return result;
      }
      // Partial match for property after dot
      const dotIndex = lastWord.lastIndexOf('.');
      if (dotIndex > 0) {
        const objectPart = lastWord.substring(0, dotIndex + 1);
        const propertyPart = lastWord.substring(dotIndex + 1);
        if (AUTOCOMPLETE_SUGGESTIONS[objectPart]) {
          const result = AUTOCOMPLETE_SUGGESTIONS[objectPart]
            .filter(field => field.toLowerCase().startsWith(propertyPart.toLowerCase()))
            .map(field => objectPart + field);
          console.log(`Partial match "${objectPart}" + "${propertyPart}":`, result);
          return result;
        }
      }
    }
    
    // Check for JavaScript keywords
    if (lastWord.length >= 2) {
      const matchingKeywords = JS_KEYWORDS.filter(kw => 
        kw.toLowerCase().startsWith(lastWord.toLowerCase())
      );
      if (matchingKeywords.length > 0) {
        console.log('Matched JS keywords:', matchingKeywords);
        return matchingKeywords;
      }
    }
    
    console.log('No matches found');
    return [];
  }, []);

  // Handle textarea input
  const handleTextareaChange = useCallback((e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setLocalValue(newValue);
    setCursorPosition(cursorPos);
    
    if (onChange) {
      onChange(newValue);
    }
    
    // Get suggestions
    const newSuggestions = getSuggestions(newValue, cursorPos);
    console.log('Setting suggestions:', newSuggestions, 'showSuggestions:', newSuggestions.length > 0);
    setSuggestions(newSuggestions);
    setShowSuggestions(newSuggestions.length > 0);
    setSelectedIndex(0);
  }, [onChange, getSuggestions]);

  // Handle keyboard navigation in suggestions
  const handleKeyDown = useCallback((e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Tab' || e.key === 'Enter') {
      if (showSuggestions) {
        e.preventDefault();
        insertSuggestion(suggestions[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [showSuggestions, suggestions, selectedIndex]);

  // Insert selected suggestion
  const insertSuggestion = useCallback((suggestion) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const textBeforeCursor = localValue.substring(0, cursorPosition);
    const textAfterCursor = localValue.substring(cursorPosition);
    const lastWord = textBeforeCursor.split(/[\s\(\)\{\}\[\];,]/).pop() || '';
    
    // Replace the partial word with the suggestion
    const newTextBefore = textBeforeCursor.substring(0, textBeforeCursor.length - lastWord.length) + suggestion;
    const newValue = newTextBefore + textAfterCursor;
    const newCursorPos = newTextBefore.length;
    
    setLocalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
    
    setShowSuggestions(false);
    
    // Set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [localValue, cursorPosition, onChange]);

  return (
    <BlockStack gap="300">
      <BlockStack gap="200">
        <Text variant="headingSm" as="h3">JavaScript Code Snippet</Text>
        <Text variant="bodySm" tone="subdued">
          Write JavaScript code that returns the value for this column. Available objects: order, billing, shipping, customer, lineItem, product, variant, custom
        </Text>
        <Text variant="bodySm" tone="magic">
          💡 Type "order.", "custom.", etc. for autocomplete suggestions. Use Tab or Enter to insert.
        </Text>
      </BlockStack>

      <div style={{ position: 'relative' }}>
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder={`Example:\nlet output = '';\nif ((custom.shipping_and_handling || '').toLowerCase().includes('ups')) {\n  output += \`UPS® \${String(custom.shipping_and_handling || '').trim()}\`;\n} else {\n  output += String(custom.shipping_and_handling || '').trim();\n}\nreturn output;`}
          style={{
            width: '100%',
            minHeight: '200px',
            padding: '12px',
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            fontSize: '13px',
            lineHeight: '1.5',
            border: '1px solid #c9cccf',
            borderRadius: '8px',
            resize: 'vertical',
            backgroundColor: '#f6f6f7'
          }}
        />
        
        {showSuggestions && suggestions.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '12px',
            marginTop: '4px',
            backgroundColor: '#fff',
            border: '1px solid #c9cccf',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            minWidth: '250px'
          }}>
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion}
                onClick={() => insertSuggestion(suggestion)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: index === selectedIndex ? '#f1f2f4' : '#fff',
                  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                  fontSize: '13px',
                  borderBottom: index < suggestions.length - 1 ? '1px solid #e1e3e5' : 'none'
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>

      <InlineStack gap="200" align="start">
        <Button onClick={handleTest} loading={testLoading} disabled={!localValue.trim()}>
          Test Snippet
        </Button>
        <Text variant="bodySm" tone="subdued">
          Test with sample data from recent orders
        </Text>
      </InlineStack>

      {testResults && (
        <Card>
          <BlockStack gap="300">
            <Text variant="headingSm">Test Results</Text>
            
            {testResults.error ? (
              <Banner tone="critical">
                <Text variant="bodySm">{testResults.error}</Text>
                {testResults.details && (
                  <Text variant="bodySm" tone="subdued">{testResults.details}</Text>
                )}
              </Banner>
            ) : testResults.results && testResults.results.length > 0 ? (
              <BlockStack gap="200">
                {testResults.results.map((result, idx) => (
                  <div key={idx} style={{ 
                    padding: '12px', 
                    background: result.success ? '#f6f6f7' : '#fff4f4',
                    borderRadius: '8px',
                    border: result.success ? '1px solid #e1e3e5' : '1px solid #ffc2c2'
                  }}>
                    <BlockStack gap="100">
                      <InlineStack gap="200" align="space-between">
                        <Text variant="bodySm" fontWeight="semibold">
                          Order: {result.orderNumber}
                        </Text>
                        <Text variant="bodySm" tone={result.success ? 'success' : 'critical'}>
                          {result.success ? '✓ Success' : '✗ Failed'}
                        </Text>
                      </InlineStack>
                      <Text variant="bodySm" tone="subdued">
                        {result.lineItemTitle}
                      </Text>
                      {result.success ? (
                        <div style={{ 
                          padding: '8px', 
                          background: '#fff', 
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '13px'
                        }}>
                          {result.output || '(empty)'}
                        </div>
                      ) : (
                        <Text variant="bodySm" tone="critical">
                          Error: {result.error}
                        </Text>
                      )}
                    </BlockStack>
                  </div>
                ))}
                <Text variant="bodySm" tone="success">
                  ✓ Tested with {testResults.totalSamples} sample order(s)
                </Text>
              </BlockStack>
            ) : (
              <Banner tone="info">
                <Text variant="bodySm">No sample data available for testing</Text>
              </Banner>
            )}
          </BlockStack>
        </Card>
      )}

      <Card>
        <BlockStack gap="200">
          <Text variant="headingSm">Available Fields Reference</Text>
          <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6' }}>
            <div><strong>order</strong>: orderNumber, orderId, createdDate, financialStatus, fulfillmentStatus, currency, shippingMethod, shippingPrice, shippingCost, shippingDiscountedPrice, customerNote, trackingNumber</div>
            <div><strong>billing</strong>: name, firstName, lastName, company, phone, address1, address2, city, province, provinceCode, zip, country, countryCode</div>
            <div><strong>shipping</strong>: name, firstName, lastName, company, phone, address1, address2, city, province, provinceCode, zip, country, countryCode</div>
            <div><strong>customer</strong>: firstName, lastName, fullName, email, phone</div>
            <div><strong>lineItem</strong>: title, sku, quantity, price, vendorSku</div>
            <div><strong>product</strong>: title, productType, vendor</div>
            <div><strong>variant</strong>: title, sku, barcode, price</div>
            <div><strong>custom</strong>: metafields and custom attributes (e.g., custom.shipping_and_handling)</div>
          </div>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
